import { Box, Typography } from '@mui/material';
import {
  ALT_TRAJECTORY_FOOTNOTE,
  altCompositeSeries,
  altTrajectoryAnnotation,
} from './altPlanContent';
import { ALTERNATE_SCALE, formatSchoolYear, type PlanStudent } from './derive';
import { CARD_SHADOW, COLORS } from './tokens';

const PLOT = { left: 90, right: 750, top: 0, bottom: 320, x0: 110, x1: 740, labelY: 345 };

function yFor(scale: number): number {
  const { scaleMin, scaleMax } = ALTERNATE_SCALE;
  const clamped = Math.min(scaleMax, Math.max(scaleMin, scale));
  return PLOT.bottom - ((clamped - scaleMin) / (scaleMax - scaleMin)) * PLOT.bottom;
}

export function AltTrajectory({ student }: { student: PlanStudent }) {
  const series = altCompositeSeries(student);
  const annotation = altTrajectoryAnnotation(student);
  const first = series[0];
  const last = series[series.length - 1];
  const span = first && last ? last.year - first.year : 0;
  const xFor = (i: number): number =>
    span === 0
      ? (PLOT.x0 + PLOT.x1) / 2
      : PLOT.x0 + ((series[i]!.year - (first as { year: number }).year) / span) * (PLOT.x1 - PLOT.x0);
  const path = series.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(p.scale).toFixed(1)}`).join(' ');
  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 600, color: COLORS.ink }}>
            Composite Trajectory
          </Typography>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', bgcolor: COLORS.bg, color: COLORS.navy, fontSize: 12, fontWeight: 700 }}>
            Alternate ACCESS scale
          </Box>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted }}>
          {`Overall Alternate ACCESS scale score${!first || !last ? '' : first.year === last.year ? ` (${formatSchoolYear(last.year)})` : ` (${formatSchoolYear(first.year)} → ${formatSchoolYear(last.year)})`}`}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: COLORS.bg, borderRadius: '8px', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted, textAlign: 'center' }}>
          {`Overall — Alternate ACCESS level & scale score (${ALTERNATE_SCALE.scaleMin}–${ALTERNATE_SCALE.scaleMax})`}
        </Typography>
        <svg
          data-testid="alt-trajectory-chart"
          viewBox="0 -12 760 372"
          width="100%"
          role="img"
          aria-label={`Overall Alternate ACCESS scale score trajectory, ${ALTERNATE_SCALE.scaleMin}–${ALTERNATE_SCALE.scaleMax}`}
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <line key={i} x1={PLOT.left} y1={i * 64} x2={PLOT.right} y2={i * 64} stroke={COLORS.divider} strokeWidth={1} />
          ))}
          {[5, 4, 3, 2, 1].map((lvl, i) => (
            <text key={lvl} x={78} y={37 + i * 64} fontSize={14} fontWeight={600} fill={COLORS.muted} textAnchor="end">
              {`L${lvl}`}
            </text>
          ))}
          {series.length > 1 && (
            <path d={path} fill="none" stroke={COLORS.navy} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          )}
          {series.map((p, i) =>
            i === series.length - 1 ? (
              <circle key={p.year} cx={xFor(i)} cy={yFor(p.scale)} r={6} fill={COLORS.navy} />
            ) : (
              <circle key={p.year} cx={xFor(i)} cy={yFor(p.scale)} r={6} fill="#fff" stroke={COLORS.navy} strokeWidth={2} />
            ),
          )}
          {last && (
            <text x={xFor(series.length - 1)} y={yFor(last.scale) < 24 ? yFor(last.scale) + 24 : yFor(last.scale) - 13} fontSize={13} fontWeight={800} fill={COLORS.ink} textAnchor="end">
              {last.scale}
            </text>
          )}
          {series.map((p, i) => (
            <text
              key={p.year}
              x={xFor(i)}
              y={PLOT.labelY}
              fontSize={13}
              fontWeight={i === series.length - 1 ? 700 : 500}
              fill={i === series.length - 1 ? COLORS.navy : COLORS.muted}
              textAnchor="middle"
            >
              {formatSchoolYear(p.year)}
            </text>
          ))}
        </svg>
      </Box>
      {annotation != null && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, borderRadius: '8px', bgcolor: COLORS.bg, outline: `1px solid ${COLORS.divider}`, outlineOffset: '-1px' }}>
          <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '9999px', bgcolor: COLORS.navy, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, fontStyle: 'italic' }}>
            i
          </Box>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink }}>
            {annotation}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, color: COLORS.muted }}>
        {ALT_TRAJECTORY_FOOTNOTE}
      </Typography>
    </Box>
  );
}
